import {observer} from 'mobx-react';
import 'moment/locale/pt-br';
import React, {useContext, useEffect, useMemo, useRef, useState} from 'react';
import styled from 'styled-components';
import {MapContext, ThemeContext} from '../../apiContexts';
import {useMap} from '../../components/map/hooks';
import useMapAlarm from '../../components/map/hooks/useMapAlarm';
import useMapDevice from '../../components/map/hooks/useMapDevice';
import useMapGeofences from '../../components/map/hooks/useMapGeofences';
import useMapLabel from '../../components/map/hooks/useMapLabel';
import useMapPosition from '../../components/map/hooks/useMapPosition';
import useMapReport from '../../components/map/hooks/useMapReport';
import useMapTrace from '../../components/map/hooks/useMapTraceV2';
import {initSearchData} from '../../components/search-box';
import useStore from '../../hooks/use-store';
import history from '../../routes/history';
import {logOut} from '../../services/authService';
import MapRightActions from '../../views/map-right-actions';
import CenterActions from '../../views/personal/bottom/center-actions';
import '../../views/personal/client.sass';
import {BottomStyle, DevicesStyle} from '../../views/personal/styles';
import Top from '../../views/personal/top';
import FloatingReport from '../../views/reports/floating-report';
import '../../views/personal/device/device.sass';
import PropTypes from 'prop-types';
import {useIntl} from 'react-intl';
import DeviceDetail from '../../views/personal/device/device-detail';
import AccumulatorModal from '../../views/personal/accumulator-modal';
import {canEditAccumulators} from '../../helpers/accumulator';
import DeviceList from '../../views/personal/device/device-list';
import useAlerts from '../../hooks/use-alerts';
import {notifyDeviceGoneAndReload} from '../../helpers/notifyDeviceGoneAndReload';
import {selectDeviceOnMap} from '../../helpers/selectDeviceOnMap';
import {goToReportRow, selectedRowCoordinate, startsNewReport} from '../../helpers/report';
import useServices from '../../hooks/use-services';
import useDevices from 'src/hooks/use-devices.js';
import {useSubjectsRealtimeUpdates} from 'src/hooks/use-subjets-realtime-updates.js';
import {useDeviceDetails} from 'src/autobahn/features/devices/queries';

const Map = styled.div`
    height: 100%;
    width: 100%;
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
`;

const PersonalContainer = (props) => {
    const {
        location,
    } = props;

    const mapRef = useRef();
    const lastRowZoom = useRef(null);
    const {
        selectedItem,
        data,
        setData,
        setSelectedRow,
        selectedRow,
        setProps,
    } = useContext(MapContext);
    const {toast} = useAlerts();
    const theme = useContext(ThemeContext);
    const {driverService, commandService} = useServices();
    const {store} = useStore();
    const {server, auth, events, drivers} = store;
    const {devices} = useDevices();
    const intl = useIntl();

    const [isOpenModalCommand, toggleModalCommand] = useState(false);
    const [commandData, setCommandData] = useState({});
    const [report, setReport] = useState();
    const [accumulatorOpen, setAccumulatorOpen] = useState(false);

    const query = useMemo(
      () => ({search: location.search, hash: location.hash}),
      [location.search],
    );

    const [searchData, setSearchData] = useState(initSearchData);

    // enable: false will prevent fetching on mount
    const {refetch: getDeviceDetails, isFetching: isFetchingTag} = useDeviceDetails(devices.selectedDevice?.id, {enabled: false});

    useSubjectsRealtimeUpdates();

    const {
        instance,
        trace,
        selectedDeviceTrace,
        toggleTrace,
        toggleSelectedDeviceTrace,
        label,
        toggleLabel,
        selectedMap,
        setSelectedMap,
        setReportData,
        showDevices,
        setShowDevices: toggleDevice,
        showGeofences,
        setShowGeofences,
        follow,
        setFollow,
        isAlarmsVisible,
        toggleAlarmsVisibility,
        createGeofenceForCurrentDevice,
        focusEventGeofence,
        clearGeofenceFocus,
    } = useMap(
      {
          target: mapRef,
          center: store.map.olCenter,
          zoom: store.map.zoom,
          tiers: store.map.olCustomTiles,
          initAllDevices: true,
          centerOnCluster: true,
          cluster: {
              distance: 25,
          },
      },
      useMapDevice,
      useMapPosition,
      useMapTrace,
      useMapLabel,
      useMapReport,
      useMapGeofences,
      useMapAlarm,
    );

    useEffect(() => {
        if (query.search) {
            setReport(query.hash.slice(1));
        } else {
            setReport(null);
        }
    }, [query]);

    // Fechar o relatorio (botao ou navegacao) tem que devolver o mapa ao normal -- sem isso
    // MapContext.data ficava com o ultimo relatorio pra sempre, e dispositivo/alarme/label/trilha
    // continuavam escondidos mesmo depois de fechar.
    useEffect(() => {
        if (!report) {
            setData([]);
        }
    }, [report]);

    useEffect(() => {
        let ignore = false;
        driverService.list().then((drivers) => {
            if (!ignore) {
                store.drivers.setDrivers(drivers);
            }
        });

        return () => {
            ignore = true;
        };
    }, []);

    useEffect(() => {
        if (instance) {
            if (selectedRow) {
                instance.setSelectedReportPoint(selectedRow.id, selectedRow.deviceId, selectedRowCoordinate(selectedRow));
            }
        }
    }, [selectedRow]);

    useEffect(() => {
        if (instance) {
            if (selectedItem) {
                const {longitude, latitude} = selectedItem;
                if (longitude && latitude) {
                    goToReportRow(instance, [longitude, latitude], lastRowZoom);
                } else {
                    instance.goTo(null, instance._instance.getView().getZoom() - 0.0001);
                }
            }
        }
    }, [selectedItem]);

    useEffect(() => {
        if (startsNewReport(data)) lastRowZoom.current = null; // a primeira linha de um relatorio novo usa o zoom do relatorio
        setReportData(data);
    }, [data]);

    useEffect(() => {
        (async () => {
            setSearchData({
                ...searchData,
                options: devices.list.map(({id, uniqueId, name}) => ({
                    id,
                    uniqueId: uniqueId,
                    name,
                })),
            });
        })();
    }, [devices.list]);

    const logoff = () => logOut();

    useEffect(() => {
        if (instance) {
            setProps({map: instance});
            const map = instance.instance;
            instance.setI18n(intl);

            map.on('click', (event) => {
                const [feature, layer] =
                map.forEachFeatureAtPixel(event.pixel, (feature, layer) => {
                    return [feature, layer];
                }) || [];

                if (!layer) return;

                const layerName = layer.get('name');
                if (layerName === 'reportLayer') {
                    const featureName = feature.get('name');
                    if (featureName !== 'line') {
                        setSelectedRow({
                            id: feature.get('id') || feature.getId(),
                            scroll: true,
                        });
                    }
                }

                if (layerName === 'devicesLayer') {
                    const features = feature.get('features');
                    if (features.length === 1) {
                        onSelectDevice(features[0].getId());
                    } else {
                        instance.spiderfyCluster(feature);
                    }
                }
            });

            map.on('pointermove', (event) => {
                map.getTargetElement().style.cursor = map.hasFeatureAtPixel(event.pixel) ?
                  'pointer' :
                  '';
            });

            map.on('pointerdrag', (event) => {
                instance.isFollowAnimating = false;
                setFollow(false);
            });

            let zoomCache;
            map.on('movestart', (event) => {
                zoomCache = event.map.getView().getZoom();
            });

            map.on('moveend', (event) => {
                if (instance.isFollowAnimating) {
                    instance.isFollowAnimating = false;
                    return;
                }
                const zoom = event.map.getView().getZoom();
                zoom !== zoomCache && setFollow(false);
            });
        }
    }, [instance]);

    const getTagPosition = async () => {
        // cancelRefetch will prevent new requests until the current one is finished
        const {isSuccess, isError, error} = await getDeviceDetails({cancelRefetch: false});
        if (isSuccess) {
            return toast.success(intl.formatMessage({id: 'devicePositionUpdateSuccess'}));
        }
        if (isError) {
            return toast.errorStatus(error, {legacy: false});
        }
    };

    const followToogle = () => {
        const status = !follow;
        setFollow(status);
        if (status && devices.selectedDevice.hasPosition()) {
            const {longitude, latitude} = devices.selectedDevice.currentPosition;
            instance.goTo([longitude, latitude]);
        }
    };

    const onChangeMap = (value) => {
        setSelectedMap(value);
    };

    const toggleGeofences = () => {
        setShowGeofences(!showGeofences);
    };

    const close = () => {
        instance.removeReports();
        history.push('/client');
    };

    /**
     * @param {number} deviceId dispositivo escolhido
     * @param {Object} [event] evento de cerca (vem do sino): o mapa vai à cerca
     *   citada com a posição do evento; sem cerca visível ao usuário, vai ao veículo (`selectDeviceOnMap`)
     */
    const onSelectDevice = (deviceId, event) => {
        if (!devices.devicesMap.has(deviceId)) {
            notifyDeviceGoneAndReload(toast, intl);
            return;
        }
        selectDeviceOnMap({devices, deviceId, event, instance, focusEventGeofence, clearGeofenceFocus, setFollow});
    };

    // executed when selecting a device from search box
    const searchDevice = (data) => {
        if (data.length) {
            const {id} = data[0];
            onSelectDevice(id);
        }
    };

    const block = (status) => {
        const {selectedDevice} = devices;
        const {id} = selectedDevice;
        const data = {deviceId: id, type: status ? 'engineStop' : 'engineResume'};
        setCommandData(data);
        toggleModalCommand(true);
    };

    const sendCommand = async () => {
        await commandService.send(commandData);
        toast.success(intl.formatMessage({id: 'commandSent'}));
        toggleModalCommand(false);
    };

    const unSelectDevice = (_) => devices.setSelected(null);

    return (
      <div id="client">
          <Map ref={mapRef}/>

          <Top
            searchData={{...searchData, onChange: searchDevice}}
            profile={auth.profile}
            logOut={logoff}
            events={events}
            onSelectDevice={onSelectDevice}
          />
          <div className="content"/>
          {report && (
            <FloatingReport type={report} location={location} close={close}/>
          )}

          <BottomStyle id="bottom">

              <DevicesStyle id="devices">
                  {devices.selectedDevice ? (
                    <DeviceDetail
                      selectedDevice={devices.selectedDevice}
                      server={server}
                      drivers={drivers}
                      theme={theme}
                      isOpenModalCommand={isOpenModalCommand}
                      toggleModalCommand={toggleModalCommand}
                      sendCommand={sendCommand}
                      commandData={commandData}
                      onEditAccumulators={canEditAccumulators(store.session) ? () => setAccumulatorOpen(true) : undefined}
                      unSelectDevice={unSelectDevice}/>
                  ) : (
                    <DeviceList devices={devices} onSelect={onSelectDevice}/>
                  )}
              </DevicesStyle>

              <AccumulatorModal
                device={devices.selectedDevice}
                isOpen={accumulatorOpen && !!devices.selectedDevice}
                close={() => setAccumulatorOpen(false)} />

              {devices.selectedDevice && (
                <CenterActions
                  trace={selectedDeviceTrace}
                  traceToggle={toggleSelectedDeviceTrace}
                  followToogle={followToogle}
                  follow={follow}
                  onBlock={block}
                  createGeofence={createGeofenceForCurrentDevice}
                  isDeviceTag={devices.selectedDevice.isTag()}
                  onPressUpdate={getTagPosition}
                  isFetchingTag={isFetchingTag}
                />
              )}

              <MapRightActions
                map={instance}
                toggleTrace={toggleTrace}
                trace={trace}
                toggleLabel={toggleLabel}
                label={label}
                onChangeMap={onChangeMap}
                selectedMap={selectedMap}
                toggleDevice={toggleDevice}
                toggleGeofences={toggleGeofences}
                showGeofences={showGeofences}
                showDevices={showDevices}
                isAlarmsVisible={isAlarmsVisible}
                toggleAlarmsVisibility={toggleAlarmsVisibility}
              />
          </BottomStyle>

      </div>
    );
};

export default observer(PersonalContainer);

PersonalContainer.propTypes = {
    location: PropTypes.any,
};
